import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, User } from 'lucide-react';
import { getNotifications, markRead, markAllRead } from '../APIs/notificationsApi';
import { trackEvent } from '../utils/analytics';

const TYPE_FILTERS = [
  { label: 'All',      value: '' },
  { label: 'Likes',    value: 'like' },
  { label: 'Comments', value: 'comment' },
  { label: 'Follows',  value: 'follow' },
];

function timeAgo(dateStr) {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60)    return `${diff}s`;
  if (diff < 3600)  return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

function NotificationRow({ notif, onRead }) {
  const navigate = useNavigate();
  const rowRef = useRef(null);
  const timerRef = useRef(null);

  // Auto-mark-read after 1 second in viewport
  useEffect(() => {
    if (notif.is_read) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          timerRef.current = setTimeout(() => onRead(notif.id), 1000);
        } else {
          clearTimeout(timerRef.current);
        }
      },
      { rootMargin: '0px', threshold: 0.5 }
    );
    if (rowRef.current) observer.observe(rowRef.current);
    return () => {
      observer.disconnect();
      clearTimeout(timerRef.current);
    };
  }, [notif.id, notif.is_read, onRead]);

  const handleClick = () => {
    trackEvent('notification_click', {
      metadata: { type: notif.notification_type, post_id: notif.direct_post ?? null },
    });
    onRead(notif.id);
    if (notif.actor?.username) {
      navigate(`/profile/${notif.actor.username}`);
    } else if (notif.direct_post) {
      navigate(`/post/${notif.direct_post}`);
    }
  };

  const actor = notif.actor || {};

  return (
    <div
      ref={rowRef}
      onClick={handleClick}
      className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors
        ${notif.is_read ? 'hover:bg-white/5' : 'bg-white/[0.04] hover:bg-white/[0.07]'}`}
    >
      {/* Actor avatar */}
      <div className="relative shrink-0">
        <div className="w-10 h-10 rounded-full overflow-hidden bg-white/10 flex items-center justify-center">
          {actor.profile_icon ? (
            <img
              src={actor.profile_icon}
              alt={actor.username}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <User className="w-4 h-4 text-white/30" />
          )}
        </div>
      </div>

      {/* Text */}
      <div className="flex-1 min-w-0">
        <p className="text-white/80 text-sm leading-snug">
          {notif.computed_text || notif.text || 'interacted with you'}
        </p>
        <p className="text-white/25 text-xs mt-0.5">{timeAgo(notif.created_at)}</p>
      </div>

      {/* Post thumbnail */}
      {notif.post_thumbnail && (
        <img
          src={notif.post_thumbnail}
          alt=""
          className="w-10 h-10 rounded-lg object-cover shrink-0 opacity-70"
        />
      )}

      {/* Unread dot */}
      {!notif.is_read && (
        <div className="w-2 h-2 bg-blue-400 rounded-full shrink-0" />
      )}
    </div>
  );
}

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [activeFilter, setActiveFilter]   = useState('');
  const [page, setPage]                   = useState(1);
  const [hasNext, setHasNext]             = useState(false);
  const [loading, setLoading]             = useState(true);
  const [loadingMore, setLoadingMore]     = useState(false);

  const load = useCallback(async (p = 1, filter = '') => {
    p === 1 ? setLoading(true) : setLoadingMore(true);
    try {
      const res = await getNotifications(p, false, filter);
      const { results, has_next } = res.data;
      setNotifications(prev => p === 1 ? results : [...prev, ...results]);
      setHasNext(has_next);
      setPage(p);
    } catch {
      // silent
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    trackEvent('notification_view');
    load(1, activeFilter);
  }, [activeFilter, load]);

  const handleFilterChange = (val) => {
    setActiveFilter(val);
  };

  const handleRead = useCallback((id) => {
    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, is_read: true } : n)
    );
    markRead(id).catch(() => {});
  }, []);

  const handleMarkAllRead = async () => {
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    await markAllRead();
  };

  const hasUnread = notifications.some(n => !n.is_read);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 sticky top-0 bg-black z-10">
        <h1 className="text-lg font-semibold">Notifications</h1>
        {hasUnread && (
          <button
            onClick={handleMarkAllRead}
            className="text-white/40 text-sm hover:text-white transition"
          >
            Mark all read
          </button>
        )}
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 px-4 py-3 border-b border-white/[0.06] overflow-x-auto"
        style={{ scrollbarWidth: 'none' }}>
        {TYPE_FILTERS.map(f => (
          <button
            key={f.value}
            onClick={() => handleFilterChange(f.value)}
            className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition
              ${activeFilter === f.value
                ? 'bg-white text-black'
                : 'bg-white/[0.07] text-white/50 hover:bg-white/[0.12] hover:text-white'
              }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="flex flex-col gap-1 py-2">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-3">
              <div className="w-10 h-10 rounded-full bg-white/[0.06] animate-pulse shrink-0" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3 bg-white/[0.06] rounded animate-pulse w-3/4" />
                <div className="h-2 bg-white/[0.04] rounded animate-pulse w-1/4" />
              </div>
            </div>
          ))}
        </div>
      ) : notifications.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <Bell className="w-12 h-12 text-white/10" />
          <p className="text-white/20 text-sm">No notifications yet</p>
        </div>
      ) : (
        <>
          <div className="divide-y divide-white/[0.04]">
            {notifications.map(n => (
              <NotificationRow key={n.id} notif={n} onRead={handleRead} />
            ))}
          </div>

          {hasNext && (
            <div className="flex justify-center py-4">
              <button
                onClick={() => load(page + 1, activeFilter)}
                disabled={loadingMore}
                className="px-6 py-2 rounded-full bg-white/[0.07] text-white/50
                  hover:bg-white/[0.12] hover:text-white text-sm transition
                  disabled:opacity-40"
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
